import { Button } from "../../ui/Button";
import { Icon } from "../../ui/Icon";
import { Surface } from "../../ui/Surface";
import { Toggle } from "../../ui/Toggle";
import { useCallback, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import { useThemeContextSelector } from "@/utils/theme";
import { FlexRowAlignCenter } from "@/components/Flex";

export type LinkEditorPanelProps = {
    initialUrl?: string;
    initialOpenInNewTab?: boolean;
    onSetLink: (url: string, openInNewTab?: boolean) => void;
};

export const useLinkEditorState = ({ initialUrl, initialOpenInNewTab, onSetLink }: LinkEditorPanelProps) => {
    const [url, setUrl] = useState(initialUrl || "");
    const [openInNewTab, setOpenInNewTab] = useState(initialOpenInNewTab || false);

    const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setUrl(event.target.value);
    }, []);

    const isValidUrl = useMemo(() => /^(\S+):(\/\/)?\S+$/.test(url), [url]);

    const handleSubmit = useCallback(
        (e: React.FormEvent) => {
            e.preventDefault();
            if (isValidUrl) {
                onSetLink(url, openInNewTab);
            }
        },
        [url, isValidUrl, openInNewTab, onSetLink]
    );

    return {
        url,
        setUrl,
        openInNewTab,
        setOpenInNewTab,
        onChange,
        handleSubmit,
        isValidUrl,
    };
};

export const LinkEditorPanel = ({ onSetLink, initialOpenInNewTab, initialUrl }: LinkEditorPanelProps) => {
    const isDarkMode = useThemeContextSelector(state => state.isDarkMode);
    const state = useLinkEditorState({ onSetLink, initialOpenInNewTab, initialUrl });

    return (
        <Surface style={{ padding: "8px" }}>
            <form
                onSubmit={state.handleSubmit}
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
                data-testid="auto-id-f73c3953-57f4-43a4-864e-511f917f5fa9">
                <Box
                    component="label"
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "8px",
                        borderRadius: "8px",
                        backgroundColor: isDarkMode ? "#171717" : "#F5F5F5",
                        cursor: "text",
                    }}>
                    <Icon name="Link" style={{ flexShrink: 0, color: isDarkMode ? "#FFFFFF" : "#000000" }} />
                    <input
                        type="url"
                        style={{
                            flex: 1,
                            border: "none",
                            backgroundColor: "transparent",
                            outline: "none",
                            minWidth: "192px",
                            color: isDarkMode ? "#FFFFFF" : "#000000",
                            fontSize: "14px",
                        }}
                        placeholder="Enter URL"
                        value={state.url}
                        onChange={state.onChange}
                        data-testid="auto-id-c14a111e-a7fd-40b8-ae22-a0cb46250dcb" />
                </Box>
                <Button
                    variant="primary"
                    buttonSize="small"
                    type="submit"
                    disabled={!state.isValidUrl}
                    data-testid="auto-id-9534eba5-544b-4ee9-a92a-596f4609968d">
                    Set Link
                </Button>
            </form>
            <div
                style={{ marginTop: "12px" }}
                data-testid="auto-id-d35b2063-1dd9-4f87-963b-6589daa34f69">
                <FlexRowAlignCenter
                    as="label"
                    style={{
                        gap: "8px",
                        fontSize: "14px",
                        fontWeight: 600,
                        cursor: "pointer",
                        userSelect: "none",
                        color: isDarkMode ? "#A3A3A3" : "#737373",
                    }}>
                    Open in new tab
                    <Toggle active={state.openInNewTab} onChange={state.setOpenInNewTab} />
                </FlexRowAlignCenter>
            </div>
        </Surface>
    );
};
